import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Banner } from '../../features/admin/banners/models/banner.model';
import { BannersApi } from '../../features/admin/banners/data-access/banners.api';

const STORAGE_KEY = 'checklist.dismissed-banners';
const DAY = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class BannerService {
  private readonly api = inject(BannersApi);
  private readonly _banners = signal<Banner[]>([]);
  readonly banners = computed(() =>
    this._banners().filter((banner) => !this.isDismissed(banner.id)),
  );

  async load(areaId: string | null): Promise<void> {
    try {
      const banners = await firstValueFrom(this.api.getActive(areaId));
      this._banners.set(
        banners.map((banner) => ({
          ...banner,
          variant: this.normalizeVariant(banner.variant),
        })),
      );
    } catch (error) {
      console.warn('Unable to load banners', error);
      this._banners.set([]);
    }
  }

  dismiss(id: string): void {
    const dismissed = this.readDismissed();
    dismissed[id] = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
    this._banners.update((banners) => banners.filter((banner) => banner.id !== id));
  }

  clearDismissed(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  private isDismissed(id: string): boolean {
    const timestamp = this.readDismissed()[id];
    return !!timestamp && Date.now() - timestamp < DAY;
  }

  private readDismissed(): Record<string, number> {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, number>;
    } catch {
      return {};
    }
  }

  private normalizeVariant(value: string): 'default' | 'information' | 'error' {
    const normalized = value?.toLowerCase();
    return normalized === 'information' || normalized === 'error' ? normalized : 'default';
  }
}
